import { Access } from './access';
import { Project } from "./project";
import { Role } from './role';

export type AuthMethod = 'password' | 'google' | 'microsoft';

export interface UserAccess {
  project: string;
  access: Access;
}

export interface UserSubscription {
  project: string
  email: boolean
  weeklyReport: boolean
  dailyReport: boolean

}

export interface ViewerPosition {
  lat: number; // Default: 0
  lon: number; // Default: 0
  zoom: number; // Default: 18
  tilt: number; // Default: 0
  projectId?: string;
}

export interface UserPersonalization {
  projectsView?: 'card' | 'list'; // Default: 'card'
  showArchivedProjects?: boolean; // Default: false
}

export interface UserSettings extends UserPersonalization {
  subscribeToReport?: string[] | string;
  [key: string]: any;
}

export const defaultUser: User = {
  id: "",
  avatar: "",
  googlePic: "",
  firstName: "",
  lastName: "",
  auth0Id: "",
  authProvider: "",
  googleId: "",
  azureId: "",
  email: "",
  password: "",
  company: "",
  language: '',
  env: '',
  theme: '',
  viewerPositions: [],
  role: {
    name: "user",
    id: ""
  },
  number: 0,
  verified: false,
  lastConnection: '',
  settings: {},
  termsApproved: false,
  welcomeViewed: false,
  projectsVisited: [],
  favouriteProjects: [],
  mfaEnabled: true,
  allowedAuthMethods: ['password', 'google', 'microsoft']
};

export interface User {
  id: string;
  _id?: string;
  // Not returned by every endpoint yet — when present and false, the account has been
  // deactivated (still resolvable, unlike a deleted account which simply disappears from
  // lists such as getUsersLight).
  active?: boolean;
  avatar: string;
  googlePic?: string;
  firstName: string;
  lastName: string;
  auth0Id?: string;
  authProvider?: string;
  googleId?: string;
  azureId?: string;
  email: string;
  password?: string;
  company?: string;
  language: string; // Default: "en-US"
  env: string; // Default: "prod"
  theme: string; // Default: "light"
  viewerPositions: ViewerPosition[];
  chatBotStatus?: boolean;
  role: Role;
  config?: string;
  job?: string;
  phone?: string;
  database?: string;
  accessToken?: string;
  refreshToken?: string;
  refreshTokenDate?: string | number;
  token?: string;
  number: number; // Default: 0
  verified: boolean; // Default: false
  currentProject?: string | Project;
  projects?: (Project | string)[];
  lastConnection: string | number; // Default: Date.now
  lastEventCheck?: string | number;
  subscriptions?: UserSubscription[];
  subscription?: UserSubscription;
  settings: UserSettings; // Default: {}
  projectSettings?: (Project | string)[];
  accessKey?: string;
  termsApproved: boolean; // Default: false
  welcomeViewed: boolean; // Default: false
  isInMailingList?: boolean;
  favouriteProjects: string[];
  projectsVisited: string[];
  accesses?: UserAccess[];
  invited?: boolean;
  hseRole?: 'publisher' | 'viewer';
  mfaEnabled?: boolean;
  allowedAuthMethods?: AuthMethod[];
}

export interface PostUser {
  firstName: string;
  lastName: string;
  email: string;
  password?: string;
  company?: string;
  language?: string; // Default: "en-US"
  env?: string; // Default: "prod"
  theme?: string; // Default: "light"
  viewerPositions?: {
    lat?: number; // Default: 0
    lon?: number; // Default: 0
    zoom?: number; // Default: 18
    tilt?: number; // Default: 0
    projectId?: string;
  }[];
  chatBotStatus?: boolean;
  role?: Role | string;
  config?: string;
  job?: string;
  phone?: string;
  database?: string;
  number?: number; // Default: 0
  verified?: boolean; // Default: false
  currentProject?: Project | string;
  projects?: (Project | string)[];
  favouriteProjects?: string[];
  projectsVisited?: (Project | string)[];
  lastConnection?: string | number; // Default: Date.now
  lastEventCheck?: string | number;
  subscriptions?: {
    project: Project | string;
    email?: boolean;
    weeklyReport?: boolean;
    dailyReport?: boolean;
  }[];
  settings?: UserSettings; // Default: {}
  projectSettings?: (Project | string)[];
  termsApproved?: boolean; // Default: false
  welcomeViewed?: boolean; // Default: false
  accesses?: UserAccess[];
  mfaEnabled?: boolean;
  allowedAuthMethods?: AuthMethod[];
}

export interface PutUser {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  company?: string;
  language?: string;
  env?: string;
  theme?: string;
  viewerPositions?: {
    lat?: number;
    lon?: number;
    zoom?: number;
    tilt?: number;
    projectId?: string;
  }[];
  chatBotStatus?: boolean;
  role?: Role | string;
  config?: string;
  job?: string;
  phone?: string;
  database?: string;
  number?: number;
  verified?: boolean;
  currentProject?: Project | string;
  projects?: (Project | string)[];
  favouriteProjects?: (Project | string)[];
  projectsVisited?: (Project | string)[];
  lastConnection?: string | number;
  lastEventCheck?: string | number;
  subscriptions?: {
    project: Project | string;
    email?: boolean;
    weeklyReport?: boolean;
    dailyReport?: boolean;
  }[];
  settings?: UserSettings;
  projectSettings?: (Project | string)[];
  termsApproved?: boolean;
  welcomeViewed?: boolean; // Default: false
  accesses?: UserAccess[];
  mfaEnabled?: boolean;
  allowedAuthMethods?: AuthMethod[];
}
